import { useState } from 'react'
import { CheckCircle2, ChevronDown, XCircle } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@/components/ui'
import { useAuditEvents } from '@/hooks'
import { cn } from '@/utils'
import type { AdapterAuditEvent } from '@/types/toolFramework'

type SuccessFilter = 'all' | 'true' | 'false'

function toSuccessBool(val: SuccessFilter): boolean | undefined {
  if (val === 'true') return true
  if (val === 'false') return false
  return undefined
}

export function AuditLog() {
  const [personaId, setPersonaId] = useState('')
  const [conversationId, setConversationId] = useState('')
  const [adapterId, setAdapterId] = useState('')
  const [successFilter, setSuccessFilter] = useState<SuccessFilter>('all')

  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [cursorStack, setCursorStack] = useState<string[]>([])

  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function handleFilterChange(setter: (v: string) => void) {
    return (v: string) => {
      setter(v)
      setCursor(undefined)
      setCursorStack([])
    }
  }

  function handleSuccessChange(val: SuccessFilter) {
    setSuccessFilter(val)
    setCursor(undefined)
    setCursorStack([])
  }

  const filters = {
    personaId: personaId || undefined,
    conversationId: conversationId || undefined,
    adapterId: adapterId || undefined,
    success: toSuccessBool(successFilter),
    cursor,
  }

  const { data, isLoading, error } = useAuditEvents(filters)

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleNext() {
    if (!data?.next_cursor) return
    setCursorStack((s) => (cursor ? [...s, cursor] : s))
    setCursor(data.next_cursor)
  }

  function handlePrev() {
    const stack = [...cursorStack]
    const prev = stack.pop()
    setCursorStack(stack)
    setCursor(prev)
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Persona ID"
          value={personaId}
          onChange={(e) => handleFilterChange(setPersonaId)(e.target.value)}
          className="w-44"
          data-testid="filter-persona-id"
        />
        <Input
          placeholder="Conversation ID"
          value={conversationId}
          onChange={(e) => handleFilterChange(setConversationId)(e.target.value)}
          className="w-44"
          data-testid="filter-conversation-id"
        />
        <Input
          placeholder="Adapter ID"
          value={adapterId}
          onChange={(e) => handleFilterChange(setAdapterId)(e.target.value)}
          className="w-44"
          data-testid="filter-adapter-id"
        />
        <Select
          value={successFilter}
          onValueChange={(v) => handleSuccessChange(v as SuccessFilter)}
        >
          <SelectTrigger className="w-40" data-testid="filter-success">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Success only</SelectItem>
            <SelectItem value="false">Failures only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load audit events'}
          </AlertDescription>
        </Alert>
      )}

      {/* Empty */}
      {!isLoading && !error && data?.events.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No audit events found.
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && data && data.events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">Created</th>
                <th className="pb-2 pr-4 font-medium">Capability</th>
                <th className="pb-2 pr-4 font-medium">Adapter</th>
                <th className="pb-2 pr-4 font-medium">Success</th>
                <th className="pb-2 pr-4 font-medium">Latency</th>
                <th className="pb-2 pr-4 font-medium">Cost</th>
                <th className="pb-2 pr-4 font-medium">Error</th>
                <th className="pb-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((event: AdapterAuditEvent) => (
                <AuditRow
                  key={event.id}
                  event={event}
                  isExpanded={expanded.has(event.id)}
                  onToggle={() => toggleExpanded(event.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && data && (
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePrev}
            disabled={cursorStack.length === 0}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleNext}
            disabled={!data.next_cursor}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

interface AuditRowProps {
  event: AdapterAuditEvent
  isExpanded: boolean
  onToggle: () => void
}

function AuditRow({ event, isExpanded, onToggle }: AuditRowProps) {
  return (
    <>
      <tr className="border-b border-border hover:bg-muted">
        <td className="py-2 pr-4 text-foreground whitespace-nowrap">
          {new Date(event.created_at).toLocaleString()}
        </td>
        <td className="py-2 pr-4 font-mono text-foreground break-all">
          {event.capability_id}
        </td>
        <td className="py-2 pr-4 font-mono text-muted-foreground">
          {event.adapter_id}
        </td>
        <td className="py-2 pr-4">
          {event.success ? (
            <CheckCircle2 className="w-4 h-4 text-success" aria-label="Success" />
          ) : (
            <XCircle className="w-4 h-4 text-destructive" aria-label="Failure" />
          )}
        </td>
        <td className="py-2 pr-4 text-muted-foreground">
          {event.latency_ms !== null ? `${event.latency_ms} ms` : '—'}
        </td>
        <td className="py-2 pr-4 text-muted-foreground">
          {event.cost_usd_cents !== null ? `$${(event.cost_usd_cents / 100).toFixed(2)}` : '—'}
        </td>
        <td className="py-2 pr-4 text-muted-foreground">
          {event.error_class ?? '—'}
        </td>
        <td className="py-2">
          <button
            onClick={onToggle}
            className="p-1 rounded-sm hover:bg-muted-foreground/10 transition-colors"
            aria-label="Toggle details"
            data-testid={`expand-${event.id}`}
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-border bg-muted">
          <td colSpan={8} className="px-4 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  args_redacted
                </p>
                <pre
                  className="whitespace-pre-wrap wrap-break-word text-xs bg-background rounded-sm p-2"
                  data-testid={`args-${event.id}`}
                >
                  {JSON.stringify(event.args_redacted, null, 2)}
                </pre>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  result_redacted
                </p>
                <pre
                  className="whitespace-pre-wrap wrap-break-word text-xs bg-background rounded-sm p-2"
                  data-testid={`result-${event.id}`}
                >
                  {JSON.stringify(event.result_redacted, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
